import React, { useEffect, useMemo, useRef, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';

import { noop } from '~/utils/noop';

import DeleteImageDialog from '../ImagesVideo/DeleteImageDialog';
import ImageEditor from '../ImagesVideo/ImageEditor';
import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';
import type { OfferBuilderMediaController } from '../useOfferBuilderMedia';

import AddRoomDialog from './AddRoomDialog';
import DeleteRoomDialog from './DeleteRoomDialog';
import RoomAccordion from './RoomAccordion';
import type { SyncRoomPolicyParams } from './RoomTabs/PoliciesTab';
import { DEFAULT_ROOM_TAB } from './types';
import type { OfferBuilderRoom, RoomTab } from './types';
import { getRoomImageCount, getRoomKey } from './utils';

interface Props {
  isLoadingProperty?: boolean;
  media: OfferBuilderMediaController;
  onOpenImagesVideo: () => void;
  offerPackages?: Array<App.Package>;
  propertyError?: unknown;
  propertyId?: string;
  propertyName: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

type DraftRoom = OfferBuilderRoom & {
  roomKey: string;
  roomTitle: string;
};

interface AddRoomValues {
  bedType: string;
  category: string;
  description: string;
  name: string;
}

type OfferPackageWithRoomPolicy = App.Package & {
  room_policy_description?: string | null;
};

function buildDraftRooms(propertyRoomTypes: Array<PropertyRoomTypeWithImages>): Array<DraftRoom> {
  return (propertyRoomTypes as Array<OfferBuilderRoom>).map((room, index) => ({
    ...room,
    room_policies: room.room_policies ?? '',
    roomKey: getRoomKey(room, index),
    roomTitle: room.name || 'Unnamed room',
  }));
}

function createDraftRoomKey() {
  return `new-room-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getAccommodationPackageRoomTypeId(offerPackage: App.Package) {
  return 'fk_room_type_id' in offerPackage ? offerPackage.fk_room_type_id : undefined;
}

function getPackageSalesforceId(offerPackage: App.Package) {
  return 'id_salesforce_external' in offerPackage ? offerPackage.id_salesforce_external : undefined;
}

function getAccommodationPackageRoomRateId(offerPackage: App.Package) {
  return 'fk_room_rate_id' in offerPackage ? offerPackage.fk_room_rate_id : undefined;
}

function getRoomRateIds(room: OfferBuilderRoom) {
  const roomRateIds = room.room_rates?.map((roomRate) => roomRate.id).filter(Boolean) ?? [];

  return roomRateIds.length > 0 ? roomRateIds : undefined;
}

function getPackageRoomPolicyDescription(offerPackage?: OfferPackageWithRoomPolicy) {
  return offerPackage?.room_policy_description ?? undefined;
}

function findMatchingPackageForRoom(room: OfferBuilderRoom, offerPackages: Array<App.Package>) {
  return offerPackages.find((offerPackage) => getAccommodationPackageRoomTypeId(offerPackage) === room.id);
}

function getSyncRoomPolicyParams(room: OfferBuilderRoom, offerPackage?: App.Package): SyncRoomPolicyParams | undefined {
  if (!offerPackage) {
    return undefined;
  }

  const dealOfferId = getPackageSalesforceId(offerPackage);
  const propertyId = ('fk_property_id' in offerPackage ? offerPackage.fk_property_id : undefined) ?? room.property_id;
  const roomTypeId = getAccommodationPackageRoomTypeId(offerPackage) ?? room.id;
  const roomRateId = getAccommodationPackageRoomRateId(offerPackage);

  if (!dealOfferId || !propertyId || !roomTypeId || !roomRateId) {
    return undefined;
  }

  return {
    dealOfferId,
    propertyId,
    roomTypeId,
    roomRateId,
    roomRateIds: getRoomRateIds(room),
  };
}

export default function OfferBuilderRooms({
  isLoadingProperty,
  media,
  onOpenImagesVideo,
  offerPackages = [],
  propertyError,
  propertyId,
  propertyName,
  propertyRoomTypes = [],
}: Props) {
  const [expandedRoomKeys, setExpandedRoomKeys] = useState<Array<string>>([]);
  const [activeRoomTabs, setActiveRoomTabs] = useState<Record<string, RoomTab>>({});
  const [searchText, setSearchText] = useState('');
  const [isAddRoomDialogOpen, setIsAddRoomDialogOpen] = useState(false);
  const [roomPendingDeletion, setRoomPendingDeletion] = useState<DraftRoom>();
  const [draftRoomTypes, setDraftRoomTypes] = useState<Array<DraftRoom>>([]);
  const hasUserEditedRoomsRef = useRef(false);
  const lastSyncedRoomSourceRef = useRef('');

  useEffect(() => {
    const roomSourceKey = `${propertyName}:${propertyRoomTypes
      .map((room, index) => {
        const offerBuilderRoom = room as OfferBuilderRoom;
        return `${getRoomKey(room, index)}:${room.name || ''}:${offerBuilderRoom.description || ''}:${
          offerBuilderRoom.room_policies || ''
        }:${getRoomRateIds(offerBuilderRoom)?.join(',') || ''}`;
      })
      .join('|')}`;

    if (hasUserEditedRoomsRef.current && lastSyncedRoomSourceRef.current.startsWith(`${propertyName}:`)) {
      return;
    }

    if (lastSyncedRoomSourceRef.current === roomSourceKey) {
      return;
    }

    hasUserEditedRoomsRef.current = false;
    lastSyncedRoomSourceRef.current = roomSourceKey;
    setDraftRoomTypes(buildDraftRooms(propertyRoomTypes));
  }, [propertyName, propertyRoomTypes]);

  useEffect(() => {
    if (hasUserEditedRoomsRef.current || draftRoomTypes.length === 0 || offerPackages.length === 0) {
      return;
    }

    setDraftRoomTypes((currentRoomTypes) => {
      let hasRoomPolicyUpdate = false;

      const nextRoomTypes = currentRoomTypes.map((room) => {
        if (room.room_policies) {
          return room;
        }

        const packageRoomPolicyDescription = getPackageRoomPolicyDescription(
          findMatchingPackageForRoom(room, offerPackages),
        );

        if (!packageRoomPolicyDescription) {
          return room;
        }

        hasRoomPolicyUpdate = true;
        return { ...room, room_policies: packageRoomPolicyDescription };
      });

      return hasRoomPolicyUpdate ? nextRoomTypes : currentRoomTypes;
    });
  }, [draftRoomTypes.length, offerPackages]);

  const filteredRooms = useMemo(() => {
    const normalisedSearchText = searchText.trim().toLowerCase();

    if (!normalisedSearchText) {
      return draftRoomTypes;
    }

    return draftRoomTypes.filter((room) =>
      [room.name, room.category, room.description].some((value) => value?.toLowerCase().includes(normalisedSearchText)),
    );
  }, [draftRoomTypes, searchText]);

  const areAllRoomsExpanded = draftRoomTypes.length > 0 && expandedRoomKeys.length === draftRoomTypes.length;

  const updateRoom = (roomKey: string, nextRoomValues: Partial<OfferBuilderRoom>) => {
    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) =>
      currentRoomTypes.map((room) => {
        if (room.roomKey !== roomKey) {
          return room;
        }

        const nextRoom = { ...room, ...nextRoomValues };

        return !room.id && nextRoomValues.name !== undefined
          ? { ...nextRoom, roomTitle: nextRoom.name || 'New room' }
          : nextRoom;
      }),
    );
  };

  const addRoom = ({ bedType, category, description, name }: AddRoomValues) => {
    const roomKey = createDraftRoomKey();
    const roomPropertyId = propertyId ?? draftRoomTypes.find((room) => room.property_id)?.property_id;
    const newRoom = {
      amenities: [],
      bed_groups: [
        {
          id: 'main-bedroom',
          configuration: [{ type: bedType, size: '', quantity: 1 }],
        },
      ],
      category: category || null,
      description,
      images: [],
      name,
      number_of_beds: [1],
      property_id: roomPropertyId,
      roomKey,
      roomTitle: name,
      source: 'admin',
    } as DraftRoom;

    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) => currentRoomTypes.concat(newRoom));
    setExpandedRoomKeys((currentRoomKeys) => Array.from(new Set([roomKey, ...currentRoomKeys])));
    setActiveRoomTabs((currentTabs) => ({ ...currentTabs, [roomKey]: DEFAULT_ROOM_TAB }));
    setIsAddRoomDialogOpen(false);
  };

  const duplicateRoom = (roomToDuplicate: DraftRoom) => {
    const roomKey = createDraftRoomKey();
    const roomName = `${roomToDuplicate.name || roomToDuplicate.roomTitle || 'Room'} copy`;
    const duplicatedRoom = {
      ...cloneDeep(roomToDuplicate),
      id: undefined,
      name: roomName,
      roomKey,
      roomTitle: roomName,
      source: 'admin',
    } as DraftRoom;

    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) => {
      const roomIndex = currentRoomTypes.findIndex((room) => room.roomKey === roomToDuplicate.roomKey);

      if (roomIndex === -1) {
        return currentRoomTypes.concat(duplicatedRoom);
      }

      return currentRoomTypes.slice(0, roomIndex + 1).concat(duplicatedRoom, currentRoomTypes.slice(roomIndex + 1));
    });
    setExpandedRoomKeys((currentRoomKeys) => Array.from(new Set([roomKey, ...currentRoomKeys])));
    setActiveRoomTabs((currentTabs) => ({
      ...currentTabs,
      [roomKey]: currentTabs[roomToDuplicate.roomKey] ?? DEFAULT_ROOM_TAB,
    }));
  };

  const deleteRoom = () => {
    if (!roomPendingDeletion) {
      return;
    }

    hasUserEditedRoomsRef.current = true;
    setDraftRoomTypes((currentRoomTypes) =>
      currentRoomTypes.filter((room) => room.roomKey !== roomPendingDeletion.roomKey),
    );
    setExpandedRoomKeys((currentRoomKeys) =>
      currentRoomKeys.filter((roomKey) => roomKey !== roomPendingDeletion.roomKey),
    );
    setActiveRoomTabs((currentTabs) => {
      const { [roomPendingDeletion.roomKey]: _removedRoomTab, ...nextTabs } = currentTabs;

      return nextTabs;
    });
    setRoomPendingDeletion(undefined);
  };

  const handleToggleAllRooms = () => {
    setExpandedRoomKeys(areAllRoomsExpanded ? [] : draftRoomTypes.map((room) => room.roomKey));
  };

  const handleExpandedChange = (roomKey: string, isExpanded: boolean) => {
    setExpandedRoomKeys((currentRoomKeys) =>
      isExpanded
        ? Array.from(new Set(currentRoomKeys.concat(roomKey)))
        : currentRoomKeys.filter((currentRoomKey) => currentRoomKey !== roomKey),
    );
  };

  if (propertyError) {
    return <Alert severity="error">Unable to load rooms for this property.</Alert>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Rooms</Typography>
          <Typography variant="body1" color="text.secondary">
            Configure room copy, bedding, amenities, and gallery for each room type.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={handleToggleAllRooms} disabled={draftRoomTypes.length === 0}>
            {areAllRoomsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button variant="contained" size="small" onClick={() => setIsAddRoomDialogOpen(true)}>
            Add room
          </Button>
        </Stack>
      </Stack>

      <AddRoomDialog
        open={isAddRoomDialogOpen}
        propertyId={propertyId}
        onAdd={addRoom}
        onClose={() => setIsAddRoomDialogOpen(false)}
      />
      <DeleteRoomDialog
        open={!!roomPendingDeletion}
        roomTitle={roomPendingDeletion?.roomTitle}
        onClose={() => setRoomPendingDeletion(undefined)}
        onConfirm={deleteRoom}
      />

      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 700 }}>
              {draftRoomTypes.length}
            </Box>{' '}
            rooms
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search rooms..."
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              disabled={isLoadingProperty || draftRoomTypes.length === 0}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button variant="contained" size="small" onClick={noop}>
              Save all
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {draftRoomTypes.length === 0 && !isLoadingProperty && (
        <Alert severity="info">No rooms are currently available for this property.</Alert>
      )}

      {filteredRooms.length === 0 && draftRoomTypes.length > 0 && (
        <Alert severity="info">No rooms match the current search.</Alert>
      )}

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Stack spacing={2}>
          {filteredRooms.map((room) => {
            const roomKey = room.roomKey;
            const activeRoomTab = activeRoomTabs[roomKey] ?? DEFAULT_ROOM_TAB;
            const matchingPackage = findMatchingPackageForRoom(room, offerPackages);

            return (
              <RoomAccordion
                key={roomKey}
                activeRoomTab={activeRoomTab}
                expanded={expandedRoomKeys.includes(roomKey)}
                imageCount={getRoomImageCount(room)}
                media={media}
                onDelete={() => setRoomPendingDeletion(room)}
                onDuplicate={() => duplicateRoom(room)}
                onOpenImagesVideo={onOpenImagesVideo}
                propertyName={propertyName}
                propertyRoomTypes={draftRoomTypes}
                room={room}
                roomKey={roomKey}
                roomTitle={room.roomTitle}
                syncBeddingConfigurationPackageId={
                  matchingPackage ? getPackageSalesforceId(matchingPackage) : undefined
                }
                syncRoomPolicyParams={getSyncRoomPolicyParams(room, matchingPackage)}
                onChange={(nextRoomValues) => updateRoom(roomKey, nextRoomValues)}
                onExpandedChange={(isExpanded) => handleExpandedChange(roomKey, isExpanded)}
                onTabChange={(tab) => setActiveRoomTabs((currentTabs) => ({ ...currentTabs, [roomKey]: tab }))}
              />
            );
          })}
        </Stack>
      </Box>
      <ImageEditor
        edit={media.roomImagePendingEdit ? media.imageEdits[media.roomImagePendingEdit.publicId] : undefined}
        image={media.roomImagePendingEdit}
        open={!!media.roomImagePendingEdit}
        roomOptions={media.roomOptions}
        onClose={() => media.setRoomImagePendingEdit(undefined)}
        onDeleteImage={media.deleteRoomImageFromEditor}
        onSave={media.saveImageEdit}
      />
      <DeleteImageDialog
        open={!!media.imagePendingDeletion}
        onClose={media.closeDeleteDialog}
        onConfirm={media.confirmDeleteImage}
      />
    </Box>
  );
}
